import { useEffect, useState, useRef } from 'react';

const processSteps = [
  {
    number: "01",
    title: "Entdeckung & Strategie",
    description: "Wir analysieren Ihre Ziele, Zielgruppe und Wettbewerb, um eine maßgeschneiderte Strategie zu entwickeln.",
    icon: "ri-search-eye-line",
    color: "#d1bc8a",
    features: ["Ausführliches Briefing-Gespräch", "Markt- und Zielgruppenanalyse", "Technische Anforderungen", "Projektplanung & Timeline"],
    tag: "Discovery"
  },
  {
    number: "02",
    title: "Design & Konzeption",
    description: "Kreative Konzepte entstehen, die Ihre Markenidentität perfekt widerspiegeln und Nutzer begeistern.",
    icon: "ri-quill-pen-line",
    color: "#c9a96e",
    features: ["Wireframes & Prototyping", "Visual Design", "UX-Optimierung", "Responsive Konzept"],
    tag: "Design"
  },
  {
    number: "03",
    title: "Entwicklung & Umsetzung",
    description: "Professionelle Programmierung mit modernsten Technologien für maximale Performance und Skalierbarkeit.",
    icon: "ri-code-s-slash-line",
    color: "#b8935a",
    features: ["Frontend-Entwicklung", "Backend-Integration", "CMS-Setup", "Performance-Optimierung"],
    tag: "Development"
  },
  {
    number: "04",
    title: "Testing & Launch",
    description: "Umfassende Tests und ein reibungsloser Launch sorgen für den perfekten Start Ihres Projekts.",
    icon: "ri-rocket-2-line",
    color: "#a07840",
    features: ["Qualitätssicherung", "Browser- & Device-Tests", "SEO-Optimierung", "Go-Live & Monitoring"],
    tag: "Launch"
  }
];

const ProcessSection = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [visibleSteps, setVisibleSteps] = useState<number[]>([]);
  const [hasEntered, setHasEntered] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const stepRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasEntered) {
            setHasEntered(true);
            processSteps.forEach((_, i) => {
              setTimeout(() => {
                setVisibleSteps(prev => [...prev, i]);
              }, i * 250);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, [hasEntered]);

  useEffect(() => {
    const stepObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const idx = stepRefs.current.indexOf(entry.target as HTMLDivElement);
            if (idx !== -1) setActiveStep(idx);
          }
        });
      },
      { threshold: 0.5, rootMargin: '-20% 0px -20% 0px' }
    );
    stepRefs.current.forEach(ref => { if (ref) stepObserver.observe(ref); });
    return () => stepObserver.disconnect();
  }, []);

  return (
    <section ref={sectionRef} id="process" className="py-20 lg:py-32 xl:py-48 bg-black relative overflow-hidden">

      {/* Ambient background glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute w-[600px] h-[600px] rounded-full blur-[120px] opacity-10 transition-all duration-1000"
          style={{
            background: `radial-gradient(circle, ${processSteps[activeStep].color}, transparent)`,
            top: `${15 + activeStep * 20}%`,
            left: '50%',
            transform: 'translateX(-50%)'
          }}
        />
      </div>

      <div className="container mx-auto px-6 lg:px-12 relative z-10">

        {/* Header */}
        <div className="mb-20 lg:mb-24 xl:mb-32 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 lg:gap-8">
          <div>
            <span className="inline-block text-[#d1bc8a] text-xs lg:text-sm font-semibold tracking-[0.3em] uppercase mb-4 lg:mb-6">
              Wie wir arbeiten
            </span>
            <h2 className="text-4xl sm:text-5xl lg:text-7xl xl:text-8xl 2xl:text-9xl font-bold text-white leading-none">
              UNSER<br />
              <span className="text-white/20">PROZESS</span>
            </h2>
          </div>
          <p className="text-base lg:text-lg xl:text-xl text-white/50 max-w-sm leading-relaxed lg:text-right">
            Von der ersten Idee bis zum erfolgreichen Launch – unser bewährter 4-Stufen-Prozess garantiert höchste Qualität.
          </p>
        </div>

        {/* Steps */}
        <div className="space-y-3 lg:space-y-4">
          {processSteps.map((step, index) => {
            const isActive = activeStep === index;
            const isVisible = visibleSteps.includes(index);

            return (
              <div
                key={index}
                ref={el => { stepRefs.current[index] = el; }}
                onClick={() => setActiveStep(index)}
                className={`group relative border rounded-2xl overflow-hidden cursor-pointer transition-all duration-700 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
                } ${isActive ? 'border-white/20' : 'border-white/5 hover:border-white/10'}`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                {/* Active background */}
                <div
                  className={`absolute inset-0 transition-opacity duration-700 ${isActive ? 'opacity-100' : 'opacity-0'}`}
                  style={{ background: `linear-gradient(135deg, ${step.color}08 0%, transparent 60%)` }}
                />

                {/* Collapsed header row */}
                <div className="relative flex items-center gap-4 lg:gap-6 xl:gap-10 px-6 lg:px-8 xl:px-12 py-6 lg:py-7 xl:py-8">
                  {/* Number */}
                  <span
                    className="text-3xl lg:text-4xl xl:text-5xl font-bold transition-colors duration-500 flex-shrink-0 w-12 lg:w-16"
                    style={{ color: isActive ? step.color : 'rgba(255,255,255,0.12)' }}
                  >
                    {step.number}
                  </span>

                  {/* Icon circle */}
                  <div
                    className="w-10 h-10 lg:w-12 lg:h-12 flex items-center justify-center rounded-full border flex-shrink-0 transition-all duration-500"
                    style={{
                      borderColor: isActive ? step.color : 'rgba(255,255,255,0.1)',
                      background: isActive ? `${step.color}18` : 'transparent'
                    }}
                  >
                    <i className={`${step.icon} text-lg lg:text-xl`} style={{ color: isActive ? step.color : 'rgba(255,255,255,0.4)' }}></i>
                  </div>

                  {/* Title */}
                  <h3 className={`text-lg sm:text-xl lg:text-2xl xl:text-3xl font-bold flex-1 transition-colors duration-500 ${isActive ? 'text-white' : 'text-white/40 group-hover:text-white/70'}`}>
                    {step.title}
                  </h3>

                  {/* Tag */}
                  <span
                    className={`hidden lg:inline-block text-xs font-semibold tracking-widest uppercase px-3 lg:px-4 py-1.5 lg:py-2 rounded-full border transition-all duration-500 flex-shrink-0`}
                    style={{
                      color: isActive ? step.color : 'rgba(255,255,255,0.2)',
                      borderColor: isActive ? `${step.color}40` : 'rgba(255,255,255,0.08)',
                      background: isActive ? `${step.color}10` : 'transparent'
                    }}
                  >
                    {step.tag}
                  </span>

                  {/* Arrow */}
                  <div className={`w-7 h-7 lg:w-8 lg:h-8 flex items-center justify-center flex-shrink-0 transition-all duration-500 ${isActive ? 'rotate-45' : 'rotate-0'}`}>
                    <i className="ri-add-line text-lg lg:text-xl" style={{ color: isActive ? step.color : 'rgba(255,255,255,0.2)' }}></i>
                  </div>
                </div>

                {/* Expanded content */}
                <div
                  className={`overflow-hidden transition-all duration-700 ease-in-out`}
                  style={{ maxHeight: isActive ? '500px' : '0px' }}
                >
                  <div className="px-6 lg:px-8 xl:px-12 pb-8 lg:pb-10 xl:pb-12">
                    {/* Divider */}
                    <div className="h-px mb-8 lg:mb-10" style={{ background: `linear-gradient(to right, ${step.color}40, transparent)` }} />

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10 xl:gap-16">
                      {/* Description */}
                      <div>
                        <p className="text-white/60 text-base lg:text-lg leading-relaxed mb-6 lg:mb-8">
                          {step.description}
                        </p>
                        {/* Big number watermark */}
                        <div
                          className="text-[6rem] lg:text-[8rem] font-bold leading-none select-none"
                          style={{ color: `${step.color}12` }}
                        >
                          {step.number}
                        </div>
                      </div>

                      {/* Features */}
                      <div className="grid grid-cols-1 gap-3 lg:gap-4">
                        {step.features.map((feature, fi) => (
                          <div
                            key={fi}
                            className="flex items-center gap-3 p-3.5 lg:p-4 rounded-xl border border-white/5 bg-white/[0.02]"
                          >
                            <div
                              className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                              style={{ background: step.color }}
                            />
                            <span className="text-white/70 text-sm leading-snug">{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Bottom progress bar */}
                <div
                  className="absolute bottom-0 left-0 h-[2px] transition-all duration-700"
                  style={{
                    width: isActive ? '100%' : '0%',
                    background: `linear-gradient(to right, ${step.color}, transparent)`
                  }}
                />
              </div>
            );
          })}
        </div>


      </div>
    </section>
  );
};

export default ProcessSection;